import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { SectionHeading, Icon, CTABand } from "../components/blocks";
import { COMPANY, REASSURANCE, IMAGES } from "../lib/data";
import { Check } from "lucide-react";

const VALUES = [
  { title: "La proximité", text: "Basés dans l'Oise, nous connaissons les maisons de la région et intervenons rapidement près de chez vous." },
  { title: "Le soin du détail", text: "Un chantier propre, rangé et respectueux de votre habitat, avec des finitions impeccables." },
  { title: "La transparence", text: "Des devis clairs, des explications simples et un interlocuteur unique du début à la fin." },
  { title: "La durabilité", text: "Des matériaux de qualité et des solutions pensées pour protéger votre bien sur le long terme." },
];

export default function APropos() {
  return (
    <div>
      <Seo
        title="À propos — Artisan couvreur dans l'Oise | Carnier Toiture et Habitat"
        description="Carnier Toiture et Habitat : 15 ans d'expérience, une équipe de 3 artisans spécialisés en toiture et amélioration de l'habitat dans l'Oise, l'Île-de-France et la Picardie."
      />

      <section className="bg-vegetal-800 text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-300">À propos</span>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-balance max-w-3xl">Un artisan passionné par le soin de votre habitat</h1>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5">
              <img src={IMAGES.roofer2} alt="L'équipe Carnier Toiture et Habitat au travail" className="w-full h-[420px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div>
              <SectionHeading overline="Notre histoire" title="15 ans d'expérience au service de votre maison" />
              <p className="mt-5 text-neutral-700 leading-relaxed">
                Depuis plus de 15 ans, <strong className="text-neutral-900">{COMPANY.name}</strong> accompagne les propriétaires dans la rénovation, l'entretien et l'amélioration de leur habitat. Spécialisés dans la toiture, nous maîtrisons également la façade, l'isolation et la protection de votre maison.
              </p>
              <p className="mt-4 text-neutral-700 leading-relaxed">
                Notre équipe à taille humaine de <strong className="text-neutral-900">3 artisans</strong> met un point d'honneur à réaliser un travail propre, soigné et durable. Nous parlons un langage simple, sans jargon, pour que vous compreniez toujours ce qui est fait chez vous.
              </p>
              <ul className="mt-6 space-y-3">
                {["Spécialistes toiture & habitat", "Basés dans l'Oise, proches de vous", "Accompagnement de A à Z", "Devis clairs et sans engagement"].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-neutral-800">
                    <span className="grid place-items-center h-6 w-6 rounded-full bg-vegetal-100 text-vegetal-600"><Check className="h-4 w-4" /></span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white/60 border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20">
          <Reveal><SectionHeading center overline="Nos valeurs" title="Ce qui guide chacune de nos interventions" /></Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 70}>
                <div className="rounded-xl bg-white border border-neutral-200 p-6 h-full shadow-sm">
                  <span className="font-heading text-4xl font-bold text-terracotta-100">0{i + 1}</span>
                  <h3 className="mt-2 font-heading text-lg font-semibold text-neutral-900">{v.title}</h3>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20">
        <Reveal><SectionHeading center overline="Nos engagements" title="Des promesses concrètes" /></Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {REASSURANCE.map((r, i) => (
            <Reveal key={r.title} delay={(i % 3) * 70}>
              <div className="flex gap-4 rounded-xl bg-white border border-neutral-200 p-6 shadow-sm h-full">
                <div className="grid place-items-center h-11 w-11 rounded-lg bg-terracotta-50 text-terracotta-600 shrink-0">
                  <Icon name={r.icon} className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-neutral-900">{r.title}</h3>
                  <p className="mt-1 text-sm text-neutral-600 leading-relaxed">{r.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABand />
    </div>
  );
}
